import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useZones } from '../hooks/useZones';
import { zonesAPI } from '../services/api';
import ZoneDetails from './ZoneDetails';

const ZoneManager = ({ onZoneChange }) => {
    const navigate = useNavigate();
    const { zones, loading: zonesLoading, refetch: refetchZones } = useZones();
    const [selectedZone, setSelectedZone] = useState(null);
    const [showZoneDetails, setShowZoneDetails] = useState(false);
    const [showCreateZone, setShowCreateZone] = useState(false);
    const [newZoneName, setNewZoneName] = useState('');
    const [message, setMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [recordCounts, setRecordCounts] = useState({});

    // Fetch record counts in batches of 5 after zones load
    useEffect(() => {
        if (!zones || zones.length === 0) return;

        const fetchRecordCounts = async () => {
            const batchSize = 5;
            for (let i = 0; i < zones.length; i += batchSize) {
                const batch = zones.slice(i, i + batchSize);
                const results = await Promise.allSettled(
                    batch.map(zone => zonesAPI.getRecordCounts(zone.id))
                );
                setRecordCounts(prev => {
                    const updated = { ...prev };
                    results.forEach((result, idx) => {
                        const zone = batch[idx];
                        if (result.status === 'fulfilled') {
                            updated[zone.id] = result.value.data;
                        }
                    });
                    return updated;
                });
            }
        };

        fetchRecordCounts();
    }, [zones]);

    // Filter and sort zones
    const filteredAndSortedZones = React.useMemo(() => {
        let filtered = [...zones];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(zone =>
                zone.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(zone => zone.status === statusFilter);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'created':
                    return new Date(b.created_on) - new Date(a.created_on);
                case 'status':
                    return a.status.localeCompare(b.status);
                default:
                    return 0;
            }
        });

        return filtered;
    }, [zones, searchTerm, statusFilter, sortBy]);

    const handleCreateZone = async (e) => {
        e.preventDefault();
        if (!newZoneName) return;

        try {
            const response = await zonesAPI.create(newZoneName);
            setMessage({ type: 'success', text: `Zona ${newZoneName} criada com sucesso!` });
            setNewZoneName('');
            setShowCreateZone(false);
            refetchZones();
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao criar zona: ${error.response?.data?.errors?.[0]?.message || error.message}` });
        }
    };

    const getTopTypes = (zoneId) => {
        const data = recordCounts[zoneId];
        if (!data || !data.types) return [];
        return Object.entries(data.types)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);
    };

    if (zonesLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <span>Carregando domínios...</span>
            </div>
        );
    }

    return (
        <div>
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">🌐 Meus Domínios ({zones.length})</h2>
                    <button className="btn btn-primary" onClick={() => setShowCreateZone(true)}>
                        ➕ Nova Zona
                    </button>
                </div>

                {message && (
                    <div className={message.type === 'success' ? 'success-message' : 'error'}>
                        {message.text}
                        <button
                            onClick={() => setMessage(null)}
                            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* Filters Section */}
                <div className="filters-section">
                    <div className="search-box" style={{ flex: 1, maxWidth: '400px' }}>
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Buscar domínio..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Status:</label>
                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">Todos</option>
                            <option value="active">Ativos</option>
                            <option value="pending">Pendentes</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Ordenar por:</label>
                        <select
                            className="filter-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Nome</option>
                            <option value="created">Data de criação</option>
                            <option value="status">Status</option>
                        </select>
                    </div>

                    <div className="filter-info">
                        {filteredAndSortedZones.length} de {zones.length} domínios
                    </div>
                </div>

                <div className="zones-grid">
                    {filteredAndSortedZones.map(zone => (
                        <div
                            key={zone.id}
                            className="zone-card"
                            onClick={() => {
                                setSelectedZone(zone);
                                setShowZoneDetails(true);
                            }}
                        >
                            <div className="zone-card-header">
                                <h3 className="zone-card-title">{zone.name}</h3>
                                <span className={`badge ${zone.status === 'active' ? 'success' : 'pending'}`}>
                                    {zone.status === 'active' ? '✓ Ativo' : '⏳ Pendente'}
                                </span>
                            </div>
                            <div className="zone-card-info">
                                <div className="zone-card-info-item">
                                    <span className="zone-card-info-label">ID:</span>
                                    <code className="zone-card-info-value">{zone.id.substring(0, 16)}...</code>
                                </div>
                                <div className="zone-card-info-item">
                                    <span className="zone-card-info-label">Criado em:</span>
                                    <span className="zone-card-info-value">
                                        {new Date(zone.created_on).toLocaleDateString('pt-BR')}
                                    </span>
                                </div>
                                <div className="zone-card-info-item">
                                    <span className="zone-card-info-label">Nameservers:</span>
                                    <span className="zone-card-info-value">
                                        {zone.name_servers?.length || 0} configurados
                                    </span>
                                </div>
                            </div>
                            <div className="zone-card-records">
                                {recordCounts[zone.id] ? (
                                    <>
                                        <span className="badge">{recordCounts[zone.id].total || 0} registros</span>
                                        {getTopTypes(zone.id).map(([type, count]) => (
                                            <span key={type} className="record-type-pill">
                                                {type}:{count}
                                            </span>
                                        ))}
                                    </>
                                ) : (
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>...</span>
                                )}
                            </div>
                            <div className="zone-card-footer">
                                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                    Clique para ver detalhes completos
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showCreateZone && (
                <div className="modal-overlay" onClick={() => setShowCreateZone(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">➕ Criar Nova Zona</h2>
                            <button className="close-btn" onClick={() => setShowCreateZone(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreateZone}>
                            <div className="form-group">
                                <label className="form-label">Nome do Domínio</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newZoneName}
                                    onChange={(e) => setNewZoneName(e.target.value)}
                                    placeholder="exemplo.com.br"
                                    required
                                />
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                    Digite o nome do domínio completo (ex: meudominio.com.br)
                                </p>
                            </div>
                            <div className="action-buttons">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateZone(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Criar Zona
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showZoneDetails && selectedZone && (
                <ZoneDetails
                    zone={selectedZone}
                    onClose={() => setShowZoneDetails(false)}
                    onUpdate={refetchZones}
                    onManageDNS={() => {
                        setShowZoneDetails(false);
                        navigate(`/zones/${selectedZone.id}`);
                    }}
                />
            )}
        </div>
    );
};

export default ZoneManager;
